import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { Badge } from '@shared/components/ui/badge';
import { ImportHistoryItem } from '../models/ImportHistoryModel';
import { ImportHistoryActions } from '../components/interaction/ImportHistoryActions';
import { cn } from '@shared/utils/utils';

interface UseImportHistoryColumnsProps {
  onViewDetails: (item: ImportHistoryItem) => void;
  onRetry: (item: ImportHistoryItem) => void;
}

/**
 * Hook que devuelve las definiciones de columnas para la tabla de histórico de importaciones.
 * Columnas visibles: ID, Ref. externa, Estado, Motivo, Pedido, Tipo, Fecha, Tiempo (ms), Mensaje error, Acciones.
 */
export const useImportHistoryColumns = ({
  onViewDetails,
  onRetry,
}: UseImportHistoryColumnsProps): ColDef<ImportHistoryItem, any>[] => {
  const { t, i18n } = useTranslation('shopsModule');

  return useMemo(
    (): ColDef<ImportHistoryItem, any>[] => [
      {
        field: 'id',
        headerName: t('importHistory.table.id', 'ID'),
        width: 90,
        minWidth: 70,
        sortable: true,
      },
      {
        field: 'external_order_ref',
        headerName: t('importHistory.table.externalRef', 'Ref. externa'),
        minWidth: 120,
        sortable: false,
        valueFormatter: (params) => params.value?.replace(/^#/, '') ?? '',
      },
      {
        field: 'status',
        headerName: t('importHistory.table.status', 'Estado'),
        minWidth: 120,
        sortable: true,
        cellRenderer: (params: { data?: ImportHistoryItem }) => {
          const status = params.data?.status;
          if (!status) return null;

          const colorMap: Record<string, string> = {
            imported: 'bg-bg-success text-success dark:text-bright-success',
            duplicated: 'bg-bg-warning text-warning',
            skipped: 'bg-body-bg text-text-secondary border border-card-border/50',
            failed: 'bg-bg-error text-error',
          };

          const labelMap: Record<string, string> = {
            imported: t('importHistory.status.imported', 'Importado'),
            duplicated: t('importHistory.status.duplicated', 'Duplicado'),
            skipped: t('importHistory.status.skipped', 'Omitido'),
            failed: t('importHistory.status.failed', 'Error'),
          };

          const colorClass = colorMap[status] || colorMap.failed;
          const label = labelMap[status] || labelMap.failed;

          return <Badge className={cn('text-xs font-medium border-0 shadow-none', colorClass)}>{label}</Badge>;
        },
      },
      {
        field: 'reason',
        headerName: t('importHistory.table.reason', 'Motivo'),
        minWidth: 140,
        sortable: false,
        cellRenderer: (params: { data?: ImportHistoryItem }) => {
          const reason = params.data?.reason;
          const status = params.data?.status;

          if (status === 'skipped' && reason === 'VALIDATION_ERROR') {
            return <span>{t('importHistory.reasons.skippedByFilters', 'Omitido por filtros')}</span>;
          }

          if (status === 'duplicated' && reason === 'ALREADY_EXISTS') {
            return <span>{t('importHistory.reasons.alreadyExists', 'Ya existe')}</span>;
          }

          if (status === 'failed' && reason === 'EXCEPTION') {
            return <span>{t('importHistory.reasons.failedByException', 'Error en proceso')}</span>;
          }

          return <span>{reason || '-'}</span>;
        },
      },
      {
        field: 'order_id',
        headerName: t('importHistory.table.orderId', 'Pedido'),
        width: 100,
        minWidth: 80,
        sortable: false,
        valueGetter: (params) => params.data?.order_id ?? '-',
      },
      {
        field: 'import_type',
        headerName: t('importHistory.table.importType', 'Tipo'),
        width: 110,
        minWidth: 90,
        sortable: false,
        valueGetter: (params) => {
          const type = params.data?.import_type;
          if (!type) return '-';
          return t(`importHistory.importType.${type}`, type);
        },
      },
      {
        field: 'imported_at',
        headerName: t('importHistory.table.importedAt', 'Fecha'),
        minWidth: 160,
        sortable: false,
        valueFormatter: (params) => {
          const v = params.value;
          if (!v) return '-';
          try {
            return new Date(v).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
          } catch {
            return String(v);
          }
        },
      },
      {
        field: 'processing_time_ms',
        headerName: t('importHistory.table.processingTimeMs', 'Tiempo (ms)'),
        width: 110,
        minWidth: 90,
        sortable: false,
        valueGetter: (params) => (params.data?.processing_time_ms ? `${params.data.processing_time_ms}ms` : '-'),
      },
      {
        headerName: '',
        colId: 'actions',
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        resizable: false,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: { data: ImportHistoryItem }) => (
          <div className="flex justify-end w-full">
            <ImportHistoryActions item={params.data} onViewDetails={onViewDetails} onRetry={onRetry} />
          </div>
        ),
      },
    ],
    [t, onViewDetails, onRetry],
  );
};
